/**
 * RÉFÉRENTIELS → SECTEURS ET SAISONS — deux listes ordonnées, dessinées pareil par la maquette (onglets `sects` et
 * `sais`) : une ligne d'ajout, des flèches pour ranger, la corbeille tant que rien ne porte la valeur.
 *
 * ⚠️ LA CORBEILLE N'EST OFFERTE QU'À `carriers === 0` — la mesure exacte de la garde du producteur, archives et ciblages
 *    de promotion compris. Ailleurs l'écran dit pourquoi la valeur reste : offrir un geste que le serveur refuse serait une
 *    option offerte non implémentée (§23.0).
 * ⚠️ L'ORDRE EST UNE DONNÉE ÉCRITE (`sortOrder`) : les flèches réécrivent les rangs par le producteur, puis RELISENT — on
 *    n'affiche jamais un ordre que le serveur n'a peut-être pas accepté.
 * ⚠️ UNE NOUVELLE SAISON SE POSE EN TÊTE (la plus récente se lit d'abord), UN NOUVEAU SECTEUR EN FIN — comme la maquette.
 * ⚠️ UNE SAISON MONTRE SES DATES, et se corrige sur place (`referential-season-dates`) — la maquette ne les dessine pas :
 *    l'âge du stock les lit, le commerçant doit voir celles qui comptent.
 */
import { useState } from 'react';
import { checkLabel, confirm, showToast } from '@orgazone/ui-web';
import { api, type ReferentialFacetEntry, type ReferentialFacetListing } from '../api';
import { t, type Translatable } from '../i18n';
import { FacetError, FacetOrphans, FacetReadFailure, rankWrites, reasonOf, swapped } from './referential-facets-shared';
import { SeasonDatesEditor, SeasonDatesLabel } from './referential-season-dates';

/** Ce qui distingue les secteurs des saisons — l'entité écrite et les mots de l'écran. */
export interface OrderedFacetConfig {
  entity: 'sector' | 'reference_list';
  /** La sorte, pour la liste générique (`season`). */
  kind?: string;
  /** Les saisons s'écrivent en chasse fixe (AH26, PE27) : des codes plus que des mots. */
  mono: boolean;
  addAtTop: boolean;
  title: Translatable;
  subtitle: Translatable;
  placeholder: Translatable;
  footnote: Translatable;
  /** « La saison « {v} » existe déjà. » */
  exists: Translatable;
  /** « utilisée — non supprimable » */
  used: Translatable;
  inactive: Translatable;
  deleteLabel: Translatable;
  deleteTitle: Translatable;
  deleteMessage: Translatable;
  created: Translatable;
  deleted: Translatable;
}

/** L'entrée d'écriture d'une ligne : son code, et sa sorte pour la liste générique. */
const entryOf = (cfg: OrderedFacetConfig, fields: Record<string, string | number | boolean | null>) =>
  (cfg.kind ? { kind: cfg.kind, ...fields } : fields) as Record<string, string | number | null>;

export function OrderedFacetTab({ cfg, listing, failure, reload }: {
  cfg: OrderedFacetConfig;
  listing: ReferentialFacetListing | null;
  failure: string | null;
  reload: () => void;
}) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** La saison dont les dates sont ouvertes à la correction. */
  const [datesOf, setDatesOf] = useState<string | null>(null);

  if (failure) return <FacetReadFailure failure={failure} />;
  if (!listing) return <p style={{ margin: 0, fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Lecture du référentiel…')}</p>;
  const rows = listing.rows;
  const order = rows.map((r) => r.code);

  /** Échange une ligne avec sa voisine : les rangs se réécrivent par le producteur, puis on relit. */
  const move = async (i: number, step: -1 | 1) => {
    const writes = rankWrites(rows, swapped(order, i, step));
    if (writes.length === 0) return;
    setBusy(true); setError(null);
    try {
      await api.writeMasterData(cfg.entity, writes.map((w) => entryOf(cfg, w)), `${cfg.entity}:${cfg.kind ?? ''}:order:${order[i]}`);
    } catch (e) {
      setError(reasonOf(e));
    }
    setBusy(false);
    reload();
  };

  /** Crée une ligne sous ce libellé, en tête ou en fin, puis range le reste autour d'elle. */
  const create = async (raw: string, atTop: boolean) => {
    const v = raw.replace(/\s+/g, ' ').trim();
    if (!v) return;
    const verdict = checkLabel(v, rows.map((r) => r.label));
    if (verdict.etat === 'exact') { setError(t(cfg.exists, { v: verdict.candidat })); return; }
    setBusy(true); setError(null);
    try {
      await api.createMasterDataOnly(cfg.entity, entryOf(cfg, { code: v, label: v, sortOrder: atTop ? 1 : order.length + 1 }),
        `${cfg.entity}:${cfg.kind ?? ''}:create:${v}`);
      // ⚠️ LE RESTE SE RANGE AUTOUR : en tête, chaque ligne descend d'un cran ; en fin, une liste jamais rangée est
      //    numérotée, sans quoi la nouvelle — rangée — passerait devant elle.
      const rest = rankWrites(rows, order, atTop ? 1 : 0);
      if (rest.length) await api.writeMasterData(cfg.entity, rest.map((w) => entryOf(cfg, w)), `${cfg.entity}:${cfg.kind ?? ''}:arrange:${v}`);
      setDraft('');
      showToast(t(cfg.created, { v }));
    } catch (e) {
      setError(reasonOf(e));
    }
    setBusy(false);
    reload();
  };

  /** Pose les dates d'une saison (deux `null` la rendent à son code), puis relit ; rend le motif d'un échec. */
  const writeDates = async (r: ReferentialFacetEntry, startDay: string | null, endDay: string | null): Promise<string | null> => {
    setBusy(true);
    try {
      await api.writeMasterData(cfg.entity, [entryOf(cfg, { code: r.code, startDay, endDay })],
        `${cfg.entity}:${cfg.kind ?? ''}:dates:${r.code}:${startDay ?? ''}-${endDay ?? ''}`);
      showToast(startDay ? t('Dates de « {v} » enregistrées', { v: r.label }) : t('« {v} » reprend les dates de son code', { v: r.label }));
      reload();
      return null;
    } catch (e) {
      return reasonOf(e);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r: ReferentialFacetEntry) => {
    const ok = await confirm({
      title: t(cfg.deleteTitle, { v: r.label }), message: t(cfg.deleteMessage),
      confirmLabel: t('Supprimer'), cancelLabel: t('Annuler'), tone: 'danger',
    });
    if (!ok) return;
    setBusy(true); setError(null);
    try {
      await api.deleteReferentielEntry(cfg.entity, entryOf(cfg, { code: r.code }), `${cfg.entity}:${cfg.kind ?? ''}:delete:${r.code}`);
      showToast(t(cfg.deleted, { v: r.label }), 'info');
    } catch (e) {
      setError(reasonOf(e));
    }
    setBusy(false);
    reload();
  };

  const arrow = (label: string, d: string, enabled: boolean, onClick: () => void) => (
    <button type="button" aria-label={label} disabled={busy || !enabled} onClick={onClick} className="oz-ref-arrow"
      style={{
        width: 22, height: 18, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: 'var(--ink-300)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, opacity: enabled ? 1 : 0.25,
        cursor: enabled && !busy ? 'pointer' : 'default',
      }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d={d} /></svg>
    </button>
  );

  return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 18px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 130, fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{t(cfg.title)}</span>
          <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t(cfg.subtitle)}</span>
        </div>
        <div style={{ display: 'flex', gap: 9, padding: '13px 18px', borderBottom: '1px solid var(--ink-100)', background: 'var(--ink-50)' }}>
          <input value={draft} aria-label={t(cfg.placeholder)} placeholder={t(cfg.placeholder)}
            onChange={(e) => { setDraft(e.target.value); setError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void create(draft, cfg.addAtTop); } }}
            className="oz-ref-input"
            style={{
              flex: 1, minWidth: 0, height: 38, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
              background: 'var(--paper)', fontSize: 'var(--t-title)', color: 'var(--ink-900)', outline: 'none',
            }} />
          <button type="button" disabled={busy} onClick={() => void create(draft, cfg.addAtTop)} className="oz-ref-add"
            style={{
              height: 38, padding: '0 15px', border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-900)', color: 'var(--paper)',
              fontSize: 'var(--t-lead)', fontWeight: 700, whiteSpace: 'nowrap', cursor: busy ? 'default' : 'pointer',
            }}>{t('Ajouter')}</button>
        </div>
        <FacetError message={error} />
        {rows.length === 0 && (
          <p style={{ margin: 0, padding: '16px 18px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('La liste est vide.')}</p>
        )}
        {rows.map((r, i) => (
          <div key={r.code} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: '0 18px', minHeight: 50, borderBottom: '1px solid var(--ink-100)' }}>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {arrow(t('Monter « {v} »', { v: r.label }), 'M12 19V5M5 12l7-7 7 7', i > 0, () => void move(i, -1))}
              {arrow(t('Descendre « {v} »', { v: r.label }), 'M12 5v14M19 12l-7 7-7-7', i < rows.length - 1, () => void move(i, 1))}
            </span>
            <span style={cfg.mono
              ? { fontFamily: 'var(--font-mono)', fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)', minWidth: 96 }
              : { flex: 1, fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-900)' }}>{r.label}</span>
            <span style={{ ...(cfg.mono ? { flex: 1 } : {}), fontSize: 'var(--t-body)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>
              {r.models > 0 ? t('{n} article', { n: r.models }) : t('aucun article')}
              {!r.isActive && <span style={{ marginLeft: 8, fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--ink-400)' }}>{t(cfg.inactive)}</span>}
            </span>
            {cfg.kind === 'season' && <SeasonDatesLabel row={r} busy={busy} onEdit={() => setDatesOf(r.code)} />}
            {/* ⚠️ SUR LES SAISONS, LA FIN DE LIGNE A UNE LARGEUR FIXE : la corbeille et « utilisée — non supprimable » n'ont pas
                la même, et les périodes qui les précèdent se décaleraient d'une ligne à l'autre. */}
            <span style={{ display: 'flex', justifyContent: 'flex-end', ...(cfg.kind === 'season' ? { minWidth: 132 } : {}) }}>
            {r.carriers === 0 ? (
              <button type="button" aria-label={t(cfg.deleteLabel, { v: r.label })} disabled={busy} onClick={() => void remove(r)} className="oz-ref-trash"
                style={{
                  width: 30, height: 30, border: 'none', borderRadius: 'var(--r-xs)', background: 'transparent', color: 'var(--ink-400)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: busy ? 'default' : 'pointer',
                }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 11v6M14 11v6" /></svg>
              </button>
            ) : (
              <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)', whiteSpace: 'nowrap' }}>{t(cfg.used)}</span>
            )}
            </span>
            {cfg.kind === 'season' && datesOf === r.code && (
              <SeasonDatesEditor row={r} busy={busy} onWrite={(a, b) => writeDates(r, a, b)} onClose={() => setDatesOf(null)} />
            )}
          </div>
        ))}
      </div>
      <p style={{ margin: '12px 2px 0', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.55 }}>{t(cfg.footnote)}</p>
      <FacetOrphans orphans={listing.orphans} mono={cfg.mono} busy={busy} onAdd={(v) => void create(v, false)} />
    </div>
  );
}
